/**
 * LoginPage.jsx
 * Path: frontend/src/pages/LoginPage.jsx
 * Description: Lakoniczny ekran logowania z obsluga pierwszego ustawienia hasla.
 * Related: hooks/useAuth.js, components/TeflisLogo.jsx
 */

import { useEffect, useState, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { useAuth } from "../hooks/useAuth";
import TeflisLogo from "../components/TeflisLogo";

export default function LoginPage() {
  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);
  const {
    login,
    finishPasswordSetup,
    loading,
    error,
    needsPasswordSetup,
    setupEmail,
  } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const setupCardRef = useRef(null);
  const newPasswordInputRef = useRef(null);

  useEffect(() => {
    if (user && token) {
      navigate("/calendar");
    }
  }, [navigate, token, user]);

  useEffect(() => {
    if (!needsPasswordSetup) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setupCardRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      newPasswordInputRef.current?.focus({ preventScroll: true });
    }, 80);

    return () => window.clearTimeout(timer);
  }, [needsPasswordSetup]);

  async function handleLogin(event) {
    event.preventDefault();
    await login(email, password);
  }

  async function handleSetup(event) {
    event.preventDefault();
    await finishPasswordSetup(newPassword, confirmPassword);
  }

  return (
    <div className={`login-page ${needsPasswordSetup ? "login-page-setup" : ""}`}>
      <div className="login-card login-card-minimal">
        <div className="login-card-head login-card-head-minimal">
          <TeflisLogo className="login-logo" />
          <div className="login-title-block">
            <p>Zaloguj sie</p>
          </div>
        </div>

        <form className="login-form" onSubmit={handleLogin}>
          <label>
            <span>Email</span>
            <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
          </label>
          <label>
            <span>Hasło</span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              placeholder="Pierwsze wejscie: puste"
            />
          </label>
          {error ? <div className="form-error">{error}</div> : null}
          <button type="submit" className="primary-button" disabled={loading}>
            {loading ? "Logowanie..." : "Zaloguj się"}
          </button>
        </form>

        {needsPasswordSetup ? (
          <form className="setup-card setup-card-elevated" onSubmit={handleSetup} ref={setupCardRef}>
            <h3>Ustaw hasło</h3>
            <p>{setupEmail || email}</p>
            <label>
              <span>Nowe hasło</span>
              <input
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                minLength={8}
                required
                ref={newPasswordInputRef}
              />
            </label>
            <label>
              <span>Powtórz hasło</span>
              <input
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                minLength={8}
                required
              />
            </label>
            <button type="submit" className="primary-button" disabled={loading}>
              {loading ? "Zapisywanie..." : "Ustaw hasło"}
            </button>
          </form>
        ) : null}
      </div>
    </div>
  );
}
